// live/narration.ts (GH5) - the client seam that turns the Haiku narration lane ON, behind a flag that
// DEFAULTS OFF so the app spends $0 until Joe explicitly enables it. This is the "narration-on flag"
// half of GH5; the HARD cost ceiling lives server-side in the Worker (coachCap.ts + coach.ts).
//
// Two guarantees hold here by construction:
//   1. FLAG OFF ⇒ NO NETWORK. narratorFor() returns null, so a surface's narration path never fetches -
//      it keeps rendering the $0 deterministic template lane (renderAllTemplates) exactly as GH3/GH4 do.
//   2. TEMPLATE IS ALWAYS THE FALLBACK. Even with the flag on, any fetch failure (offline, non-2xx, bad
//      JSON) degrades to digestFallbackCoachText(digest) - card-free, post-check-clean by construction -
//      so the app NEVER depends on the LLM being reachable.
//
// The prose lane reuses the built S13 /api/coach route (KV cache + verb-check + template fallback) via the
// @postflop/coach NarratorFn seam (narration/build.ts): production wires NarratorFn = fetchNarrator. The
// digest handed across is projectDigest(facts) - the card-free FactsDigest, the ONLY thing the LLM ever
// sees (PRODUCT_NORTH_STAR §4.1 firewall). Nothing new crosses to the model.
import { useEffect, useState } from 'react';
import type { CoachText, CoachResponse, EngineFacts, FactsDigest } from '@postflop/schema';
import type { NarratorFn } from '@postflop/coach';
import { digestFallbackCoachText } from '@postflop/coach';
import { projectDigest } from '@postflop/engine';
import { lookupNarration } from './narrationLibrary';
import type { ObserveBeat } from '../observe/data';

/** Is the pro-prose narration lane enabled at all? Read at call time (not module-init) so it is (a)
 *  togglable in tests via vi.stubEnv and (b) trivially flippable by build config. OFF unless
 *  VITE_NARRATION_ON === '1'. When ON, prose comes from the OFFLINE bundle first (GH7, $0), then the
 *  deterministic template - no network - UNLESS the live lane is ALSO explicitly enabled (below). */
export function isNarrationOn(): boolean {
  return import.meta.env.VITE_NARRATION_ON === '1';
}

/** Is the LIVE metered lane (GH5, POST /api/coach → Haiku) enabled for buckets the offline bundle does
 *  NOT cover? OFF unless VITE_NARRATION_LIVE === '1'. Default OFF means the app is fully offline at $0:
 *  a bundled bucket shows pre-authored prose, an uncovered bucket falls back to the template, and NOTHING
 *  ever hits the network. Turning this on layers GH5's metered call in ONLY for uncovered buckets (still
 *  capped + KV-cached server-side); it requires Joe's typed cost approval, exactly like GH5. */
export function isLiveNarration(): boolean {
  return import.meta.env.VITE_NARRATION_LIVE === '1';
}

/** Project a decision's full EngineFacts to the card-free digest the coach lane consumes (re-export of the
 *  engine's firewall projection; the ONLY thing that ever reaches /api/coach). */
export function digestForFacts(facts: EngineFacts): FactsDigest {
  return projectDigest(facts);
}

/** The production NarratorFn: POST the card-free digest to /api/coach and return the four CoachText slots.
 *  On ANY failure (network, non-2xx, malformed body) it silently returns the deterministic template
 *  fallback for the same digest - the request never throws, matching the Worker's own degrade-to-template
 *  contract. Same-origin relative URL: the vite proxy / Pages route forwards /api to the Worker. */
export async function fetchNarrator(digest: FactsDigest): Promise<CoachText> {
  try {
    const res = await fetch('/api/coach', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ factsDigest: digest }),
    });
    if (!res.ok) return digestFallbackCoachText(digest);
    const body = (await res.json()) as CoachResponse;
    return body.text;
  } catch {
    return digestFallbackCoachText(digest);
  }
}

/** The GH7 OFFLINE narrator: pre-authored prose from the bundled library when the spot's bucket is
 *  covered (synchronous, $0, NO network), else the live metered lane if explicitly enabled, else the
 *  deterministic template. This is the prose companion to GH4's offline hand library - with narration on
 *  and the live lane off, a live hand whose bucket is in the library shows full-sentence pro prose with
 *  ZERO runtime API calls, and every other bucket degrades silently to the $0 template. */
export function bundledNarrator(digest: FactsDigest): Promise<CoachText> {
  const bundled = lookupNarration(digest);
  if (bundled) return Promise.resolve(bundled);
  if (isLiveNarration()) return fetchNarrator(digest); // GH5 metered lane, uncovered buckets only
  return Promise.resolve(digestFallbackCoachText(digest)); // offline $0 template
}

/** The narrator a surface should use, or null when narration is off. Null ⇒ the caller skips the prose
 *  lane entirely and stays on the $0 template lane - no fetch, no cost. This is the single gate every
 *  live surface consults. When on, it returns the bundle-first offline narrator (GH7). */
export function narratorFor(): NarratorFn | null {
  return isNarrationOn() ? bundledNarrator : null;
}

// ── CX7: the DEFAULT-LIVE / FALL-BACK-BAKED selection ───────────────────────────────────────────────────
// The correction to "live = more accurate" (North Star / BUILD_PLAN §CX7). The engine's DECISION is identical
// online and offline (charts preflop, exploit-rules + equity postflop); ONLY the narration PROSE differs. So
// the baked library is the guaranteed-quality CORE, not a lesser fallback - it is what plays offline at $0 -
// and the live LLM is the VARIETY layer that a connected user defaults to. bundledNarrator above is the
// inverse (baked-first, live only for uncovered buckets); this is baked-when-offline, live-when-connected.

/** Is the browser online right now? The ONE input that flips CX7's selection between fresh-live and the baked
 *  core. `navigator.onLine` is best-effort (assumed online in non-browser/test contexts, where the fetch
 *  itself is the real test) - a fetch failure is the authoritative fallback either way. */
export function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Like fetchNarrator, but returns null (NOT the template) on ANY failure, so CX7's selector can fall back to
 *  the BAKED library - the guaranteed-quality core - before the last-resort deterministic template. */
export async function fetchNarratorOrNull(digest: FactsDigest): Promise<CoachText | null> {
  try {
    const res = await fetch('/api/coach', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ factsDigest: digest }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as CoachResponse;
    return body.text;
  } catch {
    return null;
  }
}

/**
 * CX7's default-live / fall-back-baked narrator. The selection the done-criteria describes (network off ⇒
 * baked $0; network on ⇒ fresh):
 *   - CONNECTED + live enabled ⇒ generate FRESH prose (infinite variety, metered - gated by
 *     VITE_NARRATION_LIVE, which needs Joe's typed cost approval). On ANY failure it does not ship a bad
 *     answer; it falls straight to the baked core.
 *   - OFFLINE, or live disabled, or the live call failed ⇒ the BAKED library (guaranteed-quality core, $0),
 *     and only for an uncovered bucket, the deterministic $0 template.
 * Null when narration is off (the surface stays on its own $0 lane - no fetch, no cost).
 */
export function selectingNarrator(): NarratorFn | null {
  if (!isNarrationOn()) return null;
  return async (digest: FactsDigest): Promise<CoachText> => {
    if (isLiveNarration() && isOnline()) {
      const fresh = await fetchNarratorOrNull(digest); // metered, gated by the flag
      if (fresh) return fresh;
    }
    return lookupNarration(digest) ?? digestFallbackCoachText(digest); // baked core, else $0 template
  };
}

/** The four CoachText slots → Observe prose beats, in reading order, empties skipped. Tagged "Coach" and
 *  flagged `decision` so the surface paints them distinctly from the deterministic template beats. */
export function proseBeats(text: CoachText): ObserveBeat[] {
  const order: Array<string | undefined> = [
    text.what_changed,
    text.pro_thought,
    text.villain_read,
    text.beginner_thought,
  ];
  const beats: ObserveBeat[] = [];
  for (const value of order) {
    const t = (value ?? '').trim();
    if (t) beats.push({ tokens: [t], decision: true, tag: 'Coach' });
  }
  return beats;
}

/**
 * The Observe prose lane. When narration is OFF this NEVER fetches and returns `[]` - the surface renders
 * only its $0 template beats. When ON it fetches the AI prose for `digest` (template fallback on failure,
 * inside fetchNarrator) and returns it as Observe beats to append after the template narration. Re-fetches
 * whenever the digest identity changes (i.e. a new street / new hand). Card-free: `digest` is the only
 * thing sent to /api/coach.
 */
export function useObserveProse(digest: FactsDigest | null): ObserveBeat[] {
  const [beats, setBeats] = useState<ObserveBeat[]>([]);
  const narrate = narratorFor();
  const key = digest ? JSON.stringify(digest) : null;
  useEffect(() => {
    if (!narrate || !digest) {
      setBeats([]);
      return;
    }
    let live = true;
    setBeats([]); // clear stale prose while the new spot loads
    void narrate(digest).then((text) => {
      if (live) setBeats(proseBeats(text));
    });
    return () => {
      live = false;
    };
    // key captures the digest identity; narrate is stable per render for a given flag state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return beats;
}

/** One resolved decision of the Solo debrief's CX7 library prose: the street it happened on + the four
 *  CoachText slots for it (baked offline / fresh online, via selectingNarrator). */
export interface DebriefProse {
  street: FactsDigest['street'];
  text: CoachText;
}

/**
 * CX7 debrief prose lane (Solo "watch the hand back"). When narration is OFF this NEVER fetches and returns
 * `[]` - the debrief shows only the deterministic cinematic beats, exactly as before (zero change by default).
 * When ON it resolves EACH decision's card-free digest through the default-live / fall-back-baked selection:
 * OFFLINE ⇒ the baked library ($0, no network), CONNECTED + live-enabled ⇒ fresh prose. This is the surface
 * that proves the offline library plays a narrated hand at $0. Card-free: only the digests cross to the coach.
 */
export function useDebriefProse(digests: readonly FactsDigest[]): DebriefProse[] {
  const [prose, setProse] = useState<DebriefProse[]>([]);
  const narrate = selectingNarrator();
  const key = digests.map((d) => JSON.stringify(d)).join('|');
  useEffect(() => {
    if (!narrate || digests.length === 0) {
      setProse([]);
      return;
    }
    let live = true;
    void Promise.all(digests.map((d) => narrate(d))).then((texts) => {
      if (live) setProse(texts.map((text, i) => ({ street: digests[i].street, text })));
    });
    return () => {
      live = false;
    };
    // key captures the digest identities; narrate is stable per render for a given flag state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return prose;
}
